import { AppConfig } from '@app/app.config';
import { Division } from '@app/divisions/shared/division.model';
import { HttpService } from '@app/security/shared/http.service';
import { IDivision } from '@app/divisions/shared/division.interface';
import { Injectable } from '@angular/core';
import { Logger } from '@app/logger/logger';
import { LoginStatusModel } from '@app/security/shared/login-status-change.model';
import { LoginStatusService } from '@app/security/shared/login-status.service';
import { Observable, throwError } from 'rxjs';
import { Resolve } from '@angular/router';
import { catchError, map, publishReplay, refCount } from 'rxjs/operators';

const lgSfx = '\t(divisions.service)';

@Injectable({
  providedIn: 'root'
})
export class DivisionService implements Resolve<Observable<IDivision[]>> {
  public divisions = Array<IDivision>();

  private loadDivisionsObserver;

  constructor(
    private config: AppConfig,
    private http: HttpService,
    private log: Logger,
    private loginStatusService: LoginStatusService
  ) {
    this.loginStatusService.loginStatus.subscribe((ls) => {
      this.onLoginStatusChange(ls);
    });
  }

  public getDivisions(): Observable<IDivision[]> {
    if (typeof this.loadDivisionsObserver === 'undefined' || this.loadDivisionsObserver === null) {
      this.loadDivisions();
    }

    return this.loadDivisionsObserver.pipe(
      map((divisions: Array<IDivision>) => {
        this.divisions = divisions.sort((a: IDivision, b: IDivision) => {
          if (a.abbreviation < b.abbreviation) {
            return -1;
          }

          if (a.abbreviation > b.abbreviation) {
            return 1;
          }

          return 0;
        });

        return this.divisions;
      })
    );
  }

  public loadDivisions(): Observable<Array<IDivision>> {
    this.log.log(`Loading divisions from the server ${lgSfx}`);
    const endpoint = `${this.config.getServerUri()}/api/v1/divisions`;

    if (this.loadDivisionsObserver) {
      return this.loadDivisionsObserver;
    }

    this.loadDivisionsObserver = this.http.getList<IDivision>(endpoint, Division).pipe(
      map((divisions) => {
        this.divisions = divisions;
        return this.divisions;
      }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        this.log.error(`Error loading divisions from API ${lgSfx}`, err);
        return throwError('Error loading divisions from API.');
      })
    );

    return this.loadDivisionsObserver;
  }

  private onLoginStatusChange(loginStatus: LoginStatusModel): void {
    if (loginStatus.loggedIn === false) {
      this.cleanCache();
    }
  }

  public resolve(): Observable<IDivision[]> {
    return this.loadDivisions();
  }

  private cleanCache(): void {
    this.divisions = Array<IDivision>();
    this.loadDivisionsObserver = undefined;
  }
}
